import { BUILDING_INFO_GROUPS, isSectionVisible } from '@/account/buildingInfoGroups'
import { ACCOUNT_INFO_SECTIONS } from '@/account/accountInfoSections'

export interface MenuTabItem {
  label: string
  value: string
  path: string
  show?: boolean
}

export interface MenuTabSection {
  label: string
  list: MenuTabItem[]
}

export interface MenuTabContext {
  /** '' or `/b/:bId` — building sections must carry the scope */
  scope: string
  permissions: Record<string, any>
  isAcAdmin: boolean
  features: Record<string, any>
  BuildingType: string
  PM: boolean
  activeBuildingInfoGroup?: (typeof BUILDING_INFO_GROUPS)[number]
  buildingInfoGroupSlug?: string
  accountGroupId?: string
}

/**
 * The tab strips behind each nav item, keyed by MenuTabs' `type`. Extracted
 * from MenuTabs so the sidenav search can index the same labels and paths the
 * tab shells render — a hand-copied list is how the "PM Dashboard" tile
 * drifted onto a superseded page for a month (see dashboardConfig).
 */
export const buildMenuTabList = ({
  scope,
  permissions,
  isAcAdmin,
  features,
  BuildingType,
  PM,
  activeBuildingInfoGroup,
  buildingInfoGroupSlug,
  accountGroupId,
}: MenuTabContext): Record<string, MenuTabSection> => ({
  accountInfo: {
    label: 'Account Info',
    list: ACCOUNT_INFO_SECTIONS.map((section) => ({
      label: section.label,
      value: section.slug,
      path: `/accountCreate/${accountGroupId}/${section.slug}`,
      show: !section.acAdminOnly || isAcAdmin,
    })),
  },
  buildingInfo: {
    label: activeBuildingInfoGroup?.label || 'Building Info',
    list: (activeBuildingInfoGroup?.sections || []).map((section) => ({
      label: section.label,
      value: section.slug,
      path: `${scope}/buildingInfo/${buildingInfoGroupSlug}/${section.slug}`,
      show: isSectionVisible(section, permissions, BuildingType === 'Industrial', isAcAdmin),
    })),
  },
  contacts: {
    label: 'Contacts',
    list: [
      {
        label: 'Property Team',
        value: 'contactList',
        path: `${scope}/contacts/contactList`,
        show: !!permissions?.ContactsPropertyTeams,
      },
      {
        label: 'Tenants',
        value: 'tenantLibrary',
        path: `${scope}/contacts/tenantLibrary`,
        show: !!permissions?.ContactsTenants,
        // show: ['Office', 'Retail', 'Industrial', 'LifeScience', 'Flex', 'Other'].includes(BuildingType),
      },
      {
        label: 'Tenant Contacts',
        value: 'tenantContacts',
        path: `${scope}/contacts/tenantContacts`,
      },
      {
        label: 'Residents',
        value: 'residentsLibrary',
        path: `${scope}/contacts/residentsLibrary`,
        // Residents tab hidden everywhere — keep definition/route/component as dead code for easy re-enable.
        show: false,
        // show: ['Multifamily', 'Student', 'Senior', 'Other'].includes(BuildingType),
      },
      {
        label: 'Suppliers',
        value: 'vendorList',
        path: `${scope}/contacts/vendorList`,
        show: !!permissions?.ContactsSupplier,
      },
    ],
  },
  library: {
    // Header matches the sidebar item it hangs off, like the other
    // leaf-with-tabs pages (Data Archive, Notifications & Alerts).
    //
    // The sidebar item is Activate-internal only (Menu.tsx gates it on
    // IsAcAdmin, not on these permissions), so every tab carries the same
    // bypass: an AcAdmin gets the full strip even in a group whose feature
    // flags strip the Libraries* permissions, which would otherwise leave
    // them on a "No Permission" page. Customers reaching these routes by
    // URL still see only what their permissions allow.
    label: 'QR Library',
    list: [
      {
        label: 'Signs',
        value: 'qrcodelist',
        path: `${scope}/library/qrcodelist`,
        show: isAcAdmin || !!permissions?.LibrariesSign,
      },
      // {
      //   label: '2QR',
      //   value: 'twoQRLibrary',
      //   path: `${scope}/library/twoQRLibrary`,
      // },
      // {
      //   label: 'Inspections',
      //   value: 'inspectionLibrary',
      //   path: `${scope}/library/inspectionLibrary`,
      // },
      {
        label: 'Rounds',
        value: 'roundsEngine',
        path: `${scope}/library/roundsEngine`,
        show: isAcAdmin || !!permissions?.LibrariesRound,
      },
      {
        label: 'Equipment',
        value: 'equipmentLibrary',
        path: `${scope}/library/equipmentLibrary`,
        show: isAcAdmin || !!permissions?.LibrariesEquipment,
      },
      {
        label: 'Locations',
        value: 'equipmentLocationLibrary',
        path: `${scope}/library/equipmentLocationLibrary`,
        show: isAcAdmin || !!permissions?.LibrariesLocations,
      },
      {
        label: 'Mechanical Space',
        value: 'mechanicalSpaceLibrary',
        path: `${scope}/library/mechanicalSpaceLibrary`,
        show: isAcAdmin || !!permissions?.LibrariesMechanicalSpace,
      },
      {
        // One PM Library tab whose destination follows the building's PM
        // flag: the integrated library (pmLibrary) for PM/BE/Angus buildings,
        // otherwise the Activate-managed one (activatePMLibrary).
        label: 'PM Library',
        value: PM ? 'pmLibrary' : 'activatePMLibrary',
        path: PM ? `${scope}/library/pmLibrary` : `${scope}/library/activatePMLibrary`,
        show: isAcAdmin || PM || !!permissions?.LibrariesPMLibrary,
      },
    ],
  },
  marketing: {
    label: 'Marketing',
    list: [
      {
        label: 'Marketing',
        value: 'marketing',
        path: `${scope}/marketing/customerPortal`,
      },
    ],
  },
  masterLibrary: {
    label: 'Master Library',
    list: [
      {
        label: 'Print Library',
        value: 'printLibrary',
        path: '/masterLibrary/printLibrary',
      },
      {
        label: 'Hub Library',
        value: 'printHubLibrary',
        path: '/masterLibrary/printHubLibrary',
      },
      {
        label: 'Model Hubs',
        value: 'modelHubLibrary',
        path: '/masterLibrary/modelHubLibrary',
      },
      {
        label: 'QR Library',
        value: 'printQRLibrary',
        path: '/masterLibrary/printQRLibrary',
      },
      {
        label: 'Program Library',
        value: 'programLibrary',
        path: '/masterLibrary/programLibrary',
      },
    ],
  },
  scans: {
    label: 'Scans',
    list: [
      {
        label: 'QR Location',
        value: 'QRLocation',
        path: '/scans/qrLocation',
      },
      {
        label: 'QR Name',
        value: 'QRName',
        path: '/scans/qrName',
      },
      {
        label: 'Pin Activity',
        value: 'PinActivity',
        path: '/scans/pinActivity',
      },
    ],
  },
  surveyLibrary: {
    label: 'Activate Survey',
    list: [
      {
        label: 'Survey Builder',
        value: 'surveyBuilder',
        path: '/surveyLibrary/surveyBuilder',
      },
      {
        label: 'Survey List',
        value: 'surveyList',
        path: '/surveyLibrary/surveyList',
      },
    ],
  },
  reportBuilder: {
    label: 'Report Builder',
    list: [
      {
        label: 'Report Builder',
        value: 'reportBuilder',
        path: '/reportBuilder/builder',
      },
      {
        label: 'Subscriptions',
        value: 'reportBuilderSubscriptions',
        path: '/reportBuilder/subscriptions',
      },
    ],
  },
  asset: {
    label: 'Asset',
    // Mapping Hub last, mirroring the dashboard tabs it is not part of;
    // it renders through the buildingInfo shell aliased to this list.
    // The dashboard tabs also require their intelligence module — their
    // routes are feature-guarded (routes.tsx), so an ungated tab here
    // would render only to dead-end on Access Denied.
    list: [
      {
        label: 'Capital Planning',
        value: 'capitalPlanning',
        path: `${scope}/asset/capitalPlanning`,
        show: !!permissions?.Analytics && !!permissions?.AnalyticsDashboards && !!features.CapitalPlanning,
      },
      {
        label: 'Lease',
        value: 'lease',
        path: `${scope}/asset/lease`,
        show: !!permissions?.Analytics && !!permissions?.AnalyticsDashboards && !!features.LeaseIntelligence,
      },
      {
        label: 'Roof',
        value: 'roof',
        path: `${scope}/asset/roof`,
        show: !!permissions?.Analytics && !!permissions?.AnalyticsDashboards && !!features.RoofIntelligence,
      },
      {
        label: 'Compliance',
        value: 'compliance',
        path: `${scope}/asset/compliance`,
        show: !!permissions?.Analytics && !!permissions?.AnalyticsDashboards && !!features.ComplianceIntelligence,
      },
      {
        label: 'Mapping Hub',
        value: 'mappingHub',
        path: `${scope}/buildingInfo/asset/mappingHub`,
        show: !!permissions?.ManagePropertyInfo && !!permissions?.ManageMappingHub,
      },
    ],
  },
  coi: {
    label: 'COI',
    list: [
      {
        label: 'Insurance Dashboard',
        value: 'insuranceDashboard',
        path: `${scope}/coi/insuranceDashboard`,
        show: !!permissions?.InsuranceDashboard,
      },
      {
        label: 'COI Information',
        value: 'coiInformation',
        path: `${scope}/buildingInfo/coi/coi`,
        show: !!permissions?.ManagePropertyInfo && !!permissions?.BuildingPIN,
      },
    ],
  },
  notifications: {
    label: 'Notifications & Alerts',
    // Display order per spec (Alerts, Engine, Subscriptions); Engine is
    // the default landing via the sidebar link and the route's index
    // redirect, not via tab order.
    list: [
      {
        label: 'Alerts',
        value: 'alerts',
        path: `${scope}/notifications/alerts`,
        show: !!permissions?.AnalyticsAlerts,
      },
      {
        label: 'Engine',
        value: 'engine',
        path: `${scope}/notifications/engine`,
      },
      {
        label: 'Subscriptions',
        value: 'subscriptions',
        path: `${scope}/notifications/subscriptions`,
        show: !!permissions?.AnalyticsSubscriptions,
      },
      {
        label: 'Email Log',
        value: 'emailLog',
        path: `${scope}/notifications/emailLog`,
        // Activate staff only — support tooling, not a PM surface.
        show: !!permissions?.GlobalManageCustomers,
      },
    ],
  },
  dataArchive: {
    label: 'Data Archive',
    // One nav item, four tabs (nav reorg). Terms renders through the
    // buildingInfo shell, which aliases to this list (effectiveType in
    // MenuTabs) so the chrome stays identical across all four. Migration
    // moved to the Activate section; its route stays nested here, linked
    // from the Activate sidebar group.
    list: [
      {
        label: 'Data Archive',
        value: 'dataArchive',
        path: `${scope}/dataArchive`,
        show: !!permissions?.DataArchive,
      },
      {
        label: 'Building Terms & Conditions',
        value: 'terms',
        path: `${scope}/buildingInfo/dataArchive/terms`,
        show: !!permissions?.ManagePropertyInfo && !!permissions?.BuildingTermsAndConditions,
      },
      {
        label: 'Legal',
        value: 'legal',
        path: `${scope}/dataArchive/legal`,
        show: !!permissions?.Legal,
      },
      {
        label: 'Reports',
        value: 'reports',
        path: `${scope}/dataArchive/reports`,
        show: !!permissions?.AnalyticsReports,
      },
    ],
  },
})
